import { mean } from "lodash-es";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GameEngine,
  initGameEngine,
  shutdownGameEngine,
} from "~/engine/instance";
import { useGameStore } from "~/state/game-store";
import { InventoryMineral } from "~/state/game-types";
import { TERRAIN_TYPES_MAP } from "~/terrain/types";
import { LoginPanel } from "~/ui/game/login-panel";
import { PauseMenu } from "~/ui/game/pause-menu";
import { SettingsPanel } from "~/ui/game/settings-panel";
import { Button } from "~/ui/system/button";
import { MetalWrapper } from "~/ui/system/metal-wrapper";
import { LabelPill } from "~/ui/system/pill";
import { toast } from "~/ui/system/toast";

// PlayerStats component to display player stats
const PlayerStats: React.FC = memo(() => {
  const health = useGameStore((s) => s.playerStats.health);
  const fuel = useGameStore((s) => s.playerStats.fuel);
  const heat = useGameStore((s) => s.playerStats.heat);
  const currency = useGameStore((s) => s.currency);

  return (
    <div className="flex items-center gap-3">
      <LabelPill label="HP" value={health.toFixed(1)} />
      <LabelPill label="Fuel" value={fuel.toFixed(2)} />
      <LabelPill label="Heat" value={heat.toFixed(2)} />
      <LabelPill label="Credits" value={currency} />
    </div>
  );
});

const PlayerCoordinates: React.FC = memo(() => {
  const playerX = useGameStore((s) => s.playerPosition.x);
  const playerY = useGameStore((s) => s.playerPosition.y);

  const chunkX = Math.floor(playerX / GameEngine.voxelTerrain.chunkWidth);
  const chunkY = Math.floor(playerY / GameEngine.voxelTerrain.chunkHeight);

  return (
    <>
      <LabelPill
        size="sm"
        label="XY"
        value={`${playerX.toFixed(2)}, ${playerY.toFixed(2)}`}
      />
      <LabelPill size="sm" label="Chunk XY" value={`${chunkX}, ${chunkY}`} />
    </>
  );
});

function useInterval(callback: () => void, delay: number | null) {
  const intervalRef = useRef<ReturnType<typeof setInterval>>(null);

  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    if (delay == null) return;

    intervalRef.current = setInterval(() => {
      callbackRef.current();
    }, delay);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [delay]);
}

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}
function getMemoryUsage() {
  return (
    (performance as { memory?: { usedJSHeapSize?: number } }).memory
      ?.usedJSHeapSize ?? 0
  );
}

const PerformanceStats: React.FC = memo(() => {
  const [updateFPS, setUpdateFPS] = useState(0);
  const [renderFPS, setRenderFPS] = useState(0);
  const [memoryHistory, setMemoryHistory] = useState<number[]>([]);
  useInterval(() => {
    setUpdateFPS(GameEngine.gameLoop.averageFPS());
    setRenderFPS(GameEngine.renderer.averageFPS());
    setMemoryHistory((prev) => {
      const next = [...prev, getMemoryUsage()];
      if (next.length > 5) next.shift();
      return next;
    });
  }, 1000);

  return (
    <>
      <LabelPill size="sm" label="Update FPS" value={updateFPS.toFixed(2)} />
      <LabelPill size="sm" label="Render FPS" value={renderFPS.toFixed(2)} />
      {memoryHistory.length > 0 && (
        <LabelPill
          size="sm"
          label="Memory"
          value={formatBytes(mean(memoryHistory))}
        />
      )}
    </>
  );
});

// GameControls component for inventory button
const GameControls: React.FC = memo(() => {
  const openMenu = useGameStore((s) => s.openMenu);
  const setOpenMenu = useGameStore((s) => s.setOpenMenu);

  const handleInventoryClick = () => {
    setOpenMenu(openMenu === "inventory" ? null : "inventory");
  };

  return (
    <div className="flex space-x-2 justify-end">
      <Button onClick={handleInventoryClick}>Inventory</Button>
    </div>
  );
});

// InventoryItem component for a single inventory item
type InventoryItemProps = {
  item: InventoryMineral;
  onSell: (id: InventoryMineral["id"], quantity: number) => void;
};

const InventoryItem: React.FC<InventoryItemProps> = memo(
  ({ item: { id, quantity }, onSell }) => {
    const { name, sellValue } = TERRAIN_TYPES_MAP[id] || {};
    return (
      <li className="flex justify-between items-center">
        <span>
          {name} x{quantity} (${sellValue ?? 0} each)
        </span>
        <Button size="sm" onClick={() => onSell(id, quantity)}>
          Sell All
        </Button>
      </li>
    );
  },
);

// InventoryPanel component for the inventory modal
const InventoryPanel: React.FC = memo(() => {
  const setOpenMenu = useGameStore((s) => s.setOpenMenu);
  const inventory = useGameStore((s) => s.inventory);
  const sellMineral = useGameStore((s) => s.sellMineral);

  return (
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
      <MetalWrapper className="relative w-96 h-[min(500px,90vh)] font-mono text-white flex flex-col items-stretch">
        <h2 className="text-xl font-bold border-b-2 border-gray-700 pb-4">
          Inventory
        </h2>
        {inventory.length === 0 ? (
          <p className="text-gray-300 pt-4 flex-1 overflow-y-auto">
            Inventory empty.
          </p>
        ) : (
          <ul className="space-y-2 pt-4 flex-1 overflow-y-auto">
            {inventory.map((item) => (
              <InventoryItem key={item.id} item={item} onSell={sellMineral} />
            ))}
          </ul>
        )}

        <Button
          size="sm"
          className="absolute top-4 right-4"
          onClick={() => setOpenMenu(null)}
        >
          Close
        </Button>
      </MetalWrapper>
    </div>
  );
});

// Main GameScreen component
export function GameScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlGameSaveId = searchParams.get("game_save_id") || null;

  const gameSaveIdRef = useRef<string | null>(urlGameSaveId);
  if (urlGameSaveId && urlGameSaveId !== gameSaveIdRef.current) {
    gameSaveIdRef.current = urlGameSaveId;
  }

  const navigate = useNavigate();

  const startGame = useGameStore((s) => s.startGame);
  const gameStarted = useGameStore((s) => s.gameStarted);

  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize the game engine
  useEffect(() => {
    (async () => {
      const newGameSaveId = await startGame(gameSaveIdRef.current || undefined);

      if (!gameSaveIdRef.current) {
        setSearchParams({ game_save_id: newGameSaveId }, { replace: true });
        gameSaveIdRef.current = newGameSaveId;
      }

      // Initialize engine with container
      if (!containerRef.current)
        throw new Error("Container element is not set");
      await initGameEngine({ containerElement: containerRef.current });

      useGameStore.setState({ gameStarted: true });
    })().catch((err: unknown) => {
      console.error(err);
      toast.error((err as Error).message);
      void navigate("/");
    });

    // Cleanup
    return () => {
      shutdownGameEngine();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative h-screen w-full overflow-hidden">
      {/* Game renderer takes up the full screen */}
      <div ref={containerRef} className="w-full h-full" />

      {!gameStarted && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundColor: "#87CEEB",
          }}
        >
          <h1 className="text-4xl font-bold">Loading...</h1>
        </div>
      )}

      {/* UI overlay */}
      {gameStarted && (
        <div className="absolute top-0 left-0 w-full p-4 pointer-events-none [&_button]:pointer-events-auto">
          <div className="flex gap-3 items-start">
            <PlayerStats />
            <div className="flex-1" />
            <div className="flex flex-col gap-3 items-right">
              <GameControls />
              <PlayerCoordinates />
              <PerformanceStats />
            </div>
          </div>
        </div>
      )}

      <Menus />
    </div>
  );
}

function Menus() {
  const openMenu = useGameStore((s) => s.openMenu);

  // Inventory panel (shown when toggled)
  return openMenu === "inventory" ? (
    <InventoryPanel />
  ) : openMenu === "settings" ? (
    <SettingsPanel backTo="pause" />
  ) : openMenu === "pause" ? (
    <PauseMenu />
  ) : openMenu === "login" ? (
    <LoginPanel />
  ) : null;
}
